import {
  applyVariants,
  ConfirmDialog,
  formatMoney,
  getTogether,
  type Plan,
  type Together,
  type Variant,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { day } from '../../lib/format';
import { useChange, useWhat, variantName } from '../../components/variants/Variants';
import { useReadOnly } from '../../app/readOnly';

interface Props {
  plan: Plan;
  /** The variants shown, as the trip would be with them. */
  variants: readonly Variant[];
  onClose: () => void;
  onUsed: (plan: Plan) => void;
  onFailed: () => void;
}

/** The trip with these variants: one's own figures, or the server's for several together. */
function useTogether(trip: string, variants: readonly Variant[]): Together | null {
  const [several, setSeveral] = useState<{ key: string; together: Together } | null>(null);
  const key = variants.map((v) => v.id).join(',');
  useEffect(() => {
    if (!key.includes(',')) return;
    let live = true;
    getTogether(trip, key.split(',')).then(
      (together) => {
        if (live) setSeveral({ key, together });
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [trip, key]);
  const [only] = variants;
  if (only && variants.length === 1 && only.totals && only.delta) {
    return { totals: only.totals, delta: only.delta, end: only.end };
  }
  return several?.key === key ? several.together : null;
}

/**
 * The variants shown, over the map: what each does, and the trip with them beside the plan as it
 * is — the change, the total, the length and the budget — to put into the plan after asking, or
 * put away. It folds to one line and opens again.
 */
export function Proposal({ plan, variants, onClose, onUsed, onFailed }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const what = useWhat();
  const change = useChange(plan.trip.currency);
  const [asking, setAsking] = useState(false);
  // A trip read only: what the variants would do is said, they are not used.
  const locked = useReadOnly();
  const [folded, setFolded] = useState(false);
  const together = useTogether(plan.trip.id, variants);
  const delta = change(together?.delta.expected.minor ?? 0);
  const name = variants.map(variantName).join(' + ');
  const dropped = variants.flatMap((v) => v.dropped);

  if (folded) {
    return (
      <section className="proposal proposal-folded" aria-label={t('proposal.title')}>
        <button
          type="button"
          className="proposal-unfold"
          aria-expanded="false"
          onClick={() => {
            setFolded(false);
          }}
        >
          <span className="proposal-kicker">{t('proposal.title')}</span>
          <span>{name}</span>
          {together && <span className={`delta-${delta.tone}`}>{delta.text}</span>}
          <span aria-hidden="true">▾</span>
        </button>
      </section>
    );
  }

  return (
    <section className="proposal" aria-label={t('proposal.title')}>
      <div className="proposal-head">
        <p className="proposal-kicker">{t('proposal.title')}</p>
        <span>
          <button
            type="button"
            className="icon-button icon-button-small"
            aria-label={t('proposal.fold')}
            aria-expanded="true"
            onClick={() => {
              setFolded(true);
            }}
          >
            ▴
          </button>
          <button
            type="button"
            className="icon-button icon-button-small"
            aria-label={t('proposal.close')}
            onClick={onClose}
          >
            ×
          </button>
        </span>
      </div>
      {variants.map((variant) => (
        <p key={variant.id}>
          <span className="proposal-name">{variantName(variant)}</span>{' '}
          <span className="quiet">{what(variant)}</span>
        </p>
      ))}
      {together && (
        <dl className="proposal-facts">
          <div>
            <dt>{t('proposal.change')}</dt>
            <dd className={`delta-${delta.tone}`}>{delta.text}</dd>
          </div>
          <div>
            <dt>{t('proposal.total')}</dt>
            <dd className="mono">
              {formatMoney(together.totals.expected, lang)}{' '}
              <span className="quiet">
                {t('proposal.was', { amount: formatMoney(plan.totals.expected, lang) })}
              </span>
            </dd>
          </div>
          <div>
            <dt>{t('proposal.length')}</dt>
            <dd>
              {together.end === null
                ? t('trips.days', { count: together.totals.days })
                : t('variants.days', {
                    count: together.totals.days,
                    end: day(together.end, lang),
                  })}
            </dd>
          </div>
          <div>
            <dt>{t('proposal.budget')}</dt>
            <dd className={together.totals.over ? 'proposal-over' : 'delta-saves'}>
              {together.totals.over
                ? t('variants.over', { amount: formatMoney(together.totals.over, lang) })
                : t('proposal.within', { amount: formatMoney(plan.trip.budget, lang) })}
            </dd>
          </div>
        </dl>
      )}
      {together && !locked && (
        <div className="proposal-actions">
          <button
            type="button"
            className="button button-primary button-small"
            onClick={() => {
              setAsking(true);
            }}
          >
            {t('variants.use')}
          </button>
        </div>
      )}
      {asking && (
        <ConfirmDialog
          open
          title={t('variants.useQuestion', { name })}
          body={
            dropped.length === 0
              ? t('variants.useBody')
              : t('variants.useBodyDrops', {
                  cities: dropped.map((d) => d.city).join(', '),
                  count: dropped.reduce((sum, d) => sum + d.bookings, 0),
                })
          }
          confirmLabel={t('variants.use')}
          onCancel={() => {
            setAsking(false);
          }}
          onConfirm={() => {
            setAsking(false);
            applyVariants(
              plan.trip.id,
              variants.map((v) => v.id),
            ).then(onUsed, onFailed);
          }}
        />
      )}
    </section>
  );
}
